import { AttributeSelectorType, AttributeType, LayerReferenceConfigurationDc } from "@evergis/api";
import { IJSXOption } from "@evergis/ui";
import { uniq } from "lodash";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNotification } from "../../../../../hooks/notifications/useNotification";
import { layerInfos } from "../../../../../resources/layers";

// eslint-disable-next-line max-lines-per-function
export const useAttributeSelectorOptions = (
  layerReferences?: LayerReferenceConfigurationDc[],
  attributeType?: AttributeType,
  targetAttributeName?: string,
) => {
  const { t } = useTranslation("common");
  const {
    data: referenceLayers,
    loading: referenceLayersLoading,
    error,
  } = layerInfos.use({
    input: layerReferences ? uniq(layerReferences.map(({ layerName }) => layerName)) : null,
    waitInput: true,
  });

  const dictionariesOptions = useMemo<IJSXOption[]>(
    () =>
      layerReferences && referenceLayers
        ? layerReferences
            .filter(({ layerName, referenceAttribute, targetAttribute }) => {
              const layerInfo = referenceLayers.find(({ name }) => name === layerName);
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              const type = layerInfo?.layerDefinition?.attributes[referenceAttribute]?.type;

              return type === attributeType && targetAttribute === targetAttributeName;
            })
            .map(({ layerName, referenceId, referenceAttribute }) => {
              const layerInfo = referenceLayers.find(({ name }) => name === layerName);
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              const attributeDefinition = layerInfo?.layerDefinition?.attributes[referenceAttribute];

              return {
                text: layerInfo?.alias || layerName,
                value: referenceId,
                description: attributeDefinition?.alias || referenceAttribute,
              };
            })
        : [],
    [layerReferences, referenceLayers, attributeType, targetAttributeName],
  );

  const selectorTypesOptions = useMemo<IJSXOption[]>(
    () =>
      dictionariesOptions && dictionariesOptions.length > 0
        ? [
            {
              text: t("input"),
              value: AttributeSelectorType.None,
            },
            {
              text: t("selectFromDictionary"),
              value: AttributeSelectorType.SelectFromHandBook,
            },
            {
              text: t("viewFromDictionary"),
              value: AttributeSelectorType.ViewHandBook,
            },
          ]
        : [
            {
              text: t("input"),
              value: AttributeSelectorType.None,
            },
          ],
    [t, dictionariesOptions],
  );

  useNotification(error, {
    error: true,
    title: t("dictionariesLoadingError"),
    description: error?.message,
  });

  return {
    referenceLayers,
    referenceLayersLoading,
    selectorTypesOptions,
    dictionariesOptions,
  };
};
